import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { translateBatch, translatePhrase } from '@/api/translationApi';

const LanguageContext = createContext(null);

// UI label translations (static, no API needed)
const uiTranslations = {
  en: {
    literalMeaning: "Literal Meaning",
    actualMeaning: "Actual Meaning",
    examplesInContext: "Examples in Context",
    helpfulTips: "Helpful Tips",
    phraseThesaurus: "Phrase Thesaurus",
    similarWords: "Similar words and synonyms for this phrase",
    relatedPhrases: "Related Phrases",
    clickToExplore: "Click to explore related phrases in our collection",
    searchPlaceholder: "Enter a phrase you'd like to understand",
    searchResults: "Search Results",
    found: "Found",
    phrase: "phrase",
    phrases: "phrases",
    for: "for",
    clearSearch: "Clear Search",
    search: "Search",
    noPhrasesFound: "No phrases found",
    tryDifferent: "Try different keywords or browse categories below",
    requestPhrase: "Request this phrase",
    browseByCategory: "Browse by Category",
    selectCategory: "Select a category to explore phrases",
    selectSubcategory: "Select a subcategory to view phrases",
    backToCategories: "Back to Categories",
    backTo: "Back to",
    inThisCategory: "in this category",
    noPhrases: "No phrases yet",
    comingSoon: "This category is coming soon! Help us by suggesting phrases.",
    suggestPhrase: "Suggest a phrase",
    definitionsLanguage: "Definitions in English",
    toggleLanguageLabel: "Toggle definitions language",
    translating: "Translating...",
    backToAllPhrases: "Back to all phrases",
    back: "Back",
    // Hero section translations
    heroTagline: "Understand implicit communication with clarity",
    heroTitle1: "Decode Hidden Meanings in",
    heroTitle2: "Everyday Conversations",
    heroDescription: "A supportive tool of actual definitions for implied meanings, idioms, sarcasm, generational, regional, and social phrases that native neurotypical people often use. This tool was built by a neurodivergent single mom of 4 neurodivergent kids, but this app is for anyone and everyone who finds it helpful!",
    heroSearchPrompt: "Search for a phrase or scroll down to view phrases by category",
    // Navigation
    previous: "Previous",
    next: "Next",
    // Header
    decodeCommunication: "Decode Communication",
    menu: "Menu",
    home: "Home",
    about: "About",
    feedbackRequest: "Feedback / Request Phrase",
    dinoLlamaTimer: "Dino Llama Fun Timer",
    todoList: "To-Do List",
    subscription: "Subscription",
    manageSubscription: "Manage Subscription",
    signIn: "Sign In",
    signOut: "Sign Out",
    accountSettings: "Account Settings",
    leaderboard: "Leaderboard",
    trial: "Trial",
    premium: "VIP Access",
    // Multi-category
    foundInMultipleCategories: "Found in multiple categories:",
    meaningsByCategory: "Meanings by Category",
    meaning: "Meaning",
    context: "Context",
    examples: "Examples",
    tips: "Tips",
    // Feedback Page
    feedbackTitle: "Share Your Feedback",
    feedbackDescription: "Help us improve or request new phrases",
    feedbackTab: "Feedback",
    requestPhraseTab: "Request Phrase",
    yourName: "Your Name",
    yourEmail: "Your Email",
    yourFeedback: "Your Feedback",
    shareThoughts: "Share your thoughts, suggestions, or report issues...",
    submitFeedback: "Submit Feedback",
    phraseYouHeard: "Phrase You Heard",
    enterPhrase: "Enter the phrase you'd like us to add...",
    whatYouThink: "What do you think it literally means?",
    whatItActuallyMeans: "What do you think it actually means?",
    additionalContext: "Additional Context",
    whereYouHeard: "Where did you hear it? Any other helpful information...",
    submitRequest: "Submit Request",
    // About Page
    aboutTitle: "About ClearCommunicationApp",
    aboutAppDescription: "Understanding implicit communication for everyone",
    // Timer Page
    timerTitle: "Dino Llama Fun Timer",
    timerDescription: "A playful productivity timer",
    setTimer: "Set Timer",
    minutes: "minutes",
    startTimer: "Start Timer",
    pauseTimer: "Pause Timer",
    resetTimer: "Reset Timer",
    timerFinished: "Timer finished!",
    // Todo Page
    todoTitle: "To-Do List",
    todoDescription: "Keep track of your tasks",
    addTask: "Add Task",
    newTask: "New task...",
    noTasks: "No tasks yet. Add one above!",
    clearCompleted: "Clear Completed",
    // Subscription & VIP Access
    vipAccess: "VIP Access",
    definitionPreviewsAvailable: "Definition previews available with subscription",
    subscribeToUnlock: "Subscribe to unlock",
    startFreeTrial: "Start Free Trial",
    freeTrialDays: "7-day free trial",
    thenPerMonth: "then $1.99/month",
    unlimitedAccess: "Unlimited access to all phrases",
    cancelAnytime: "Cancel anytime",
    freeViewsRemaining: "free views remaining",
    upgradeForUnlimited: "Upgrade for unlimited access",
    becomeVipToSee: "To view definition previews become VIP (click to see full definition now)",
    // Free phrase limit
    freeLimitReached: "Free Limit Reached",
    freePhrasesExhausted: "Free Phrases Exhausted",
    youveViewedFree: "You've viewed",
    freePhrases: "free phrases",
    upgradeToUnlock: "Upgrade to unlock all",
    plusPhrases: "+ phrases!",
    subscribeToUnlockAll: "Subscribe to unlock unlimited access to all",
    phraseDefinitions: "phrase definitions",
    startSevenDayTrial: "Start 7-Day Free Trial",
    starting: "Starting...",
    subscribePerMonth: "Subscribe for $1.99/month",
    signInToContinue: "to continue with your free trial",
    maybeLater: "Maybe Later",
    sevenDayTrialIncluded: "7-day free trial included • Cancel anytime",
    freePhrasesRemaining: "free phrases remaining",
    freePhraseRemaining: "free phrase remaining",
    // Daily limit
    dailyLimitReached: "Daily Limit Reached",
    dailyPhrasesExhausted: "Daily Phrases Exhausted",
    freePhrasesToday: "free phrases today",
    dailyLimitMessage: "Go ad-free with VIP",
    comeBackTomorrow: "Enjoy a clean, ad-free experience with VIP!",
    goVipUnlimited: "Go VIP for an ad-free experience!",
    dailyViewsReset: "Daily views reset at midnight",
    // FAQs
    faqTitle: "Frequently Asked Questions",
    faqHowTrialWorks: "How does the free trial work?",
    faqTrialAnswer: "When you start your VIP subscription, you get 7 days completely free. Your card won't be charged until day 8. You can cancel anytime during the trial.",
    faqWhatIncluded: "What's included?",
    faqIncludedAnswer: "With VIP Access you get an ad-free experience, plus early access to new features and premium support.",
    // App Store
    comingSoonAppStore: "Coming soon to an app store near you!",
    // Feedback form fields
    enterName: "Enter your name",
    emailAddress: "Email Address",
    emailPlaceholder: "your@email.com",
    feedbackPlaceholder: "Share your thoughts, suggestions, or report issues...",
    sending: "Sending...",
    phrasePlaceholder: "e.g., 'We should do this again sometime'",
    literalMeaningPlaceholder: "What the words literally say",
    impliedMeaningPlaceholder: "What the speaker actually means",
    additionalNotes: "Additional Notes",
    notesPlaceholder: "Context, examples, or where you heard this phrase...",
    submitPhraseRequest: "Submit Phrase Request",
    emailDirectly: "You can also email us directly at",
    optional: "(Optional)",
    // FAQs Page
    faqsPage: "FAQs",
    faqSectionBreakdown: "What are each of the sections in the phrase library for?",
    faqSectionBreakdownAnswer: "Each phrase entry includes several sections to help you fully understand the phrase:\n\n• **Literal Meaning** — What the words technically say (the dictionary definition)\n\n• **Actual Meaning** — What the speaker really means (the implied or slang meaning)\n\n• **Context** — When and how the phrase is typically used\n\n• **Examples** — Real-world usage scenarios showing the phrase in action\n\n• **Tips** — Helpful advice for understanding and responding appropriately",
    faqWhyBuilt: "Why was this app built?",
    faqWhyBuiltAnswer: "This app was built by a neurodivergent mom who understands the challenges of decoding implicit communication. It's designed to help anyone who wants to better understand the hidden meanings in everyday conversations.",
    // Thought Interpreter
    thoughtInterpreter: "Thought Interpreter",
    interpreterSubtitle: "Type what you're trying to convey and Detective Dino will help you rephrase it clearly",
    remainingToday: "remaining today",
    inputLanguage: "Input Language",
    outputLanguage: "Output Language",
    whatDoYouWantToSay: "What do you want to say?",
    interpreterPlaceholder: "I can't work this weekend because I have a family event",
    tone: "Tone",
    interpret: "Interpret",
    interpreting: "Interpreting...",
    interpretedVersion: "Interpreted Version",
    // Neurotypical Decoder (May 2026)
    modeRephrase: "Rephrase my thought",
    modeDecode: "Decode their words",
    decoderTitle: "Neurotypical Decoder",
    decoderSubtitle: "Paste what they said. Get what they actually meant + a reply you can mirror back.",
    decoderInputLabel: "Paste their message",
    decoderPlaceholder: "Let's circle back on this — would be great if you could revisit the deliverable.",
    decode: "Decode",
    decoding: "Decoding…",
    decoderHeading: "What they actually mean",
    decoderLiteralLabel: "Literal meaning",
    decoderIntentLabel: "Why they said it that way",
    decoderResponseLabel: "Reply you can use",
    decoderDisclaimer: "AI-assisted reading — context matters. Use as a starting point, not a verdict.",
    copy: "Copy",
    copied: "Copied",
    tryAgain: "Try Again",
    recentHistory: "Recent History",
    dailyLimitVip: "Daily limit reached. Upgrade to VIP for unlimited access!",
    pleaseTypeSomething: "Please type something first",
    failedToInterpret: "Failed to interpret. Please try again.",
    save: "Save",
    saved: "Saved",
    savedToFavorites: "Saved to your rephrases",
    alreadySaved: "Already saved",
    signInToSave: "Sign in to save rephrases",
    failedToSave: "Could not save. Please try again.",
    myRephrases: "Saved thoughts that were interpreted",
    noSavedRephrases: "You haven't saved any rephrases yet. Use the Interpreter and tap Save to keep scripts you love.",
    deleteRephrase: "Delete rephrase",
    confirmDeleteRephrase: "Delete this saved rephrase?",
    rephraseDeleted: "Rephrase deleted",
    // Tones
    toneCasual: "Casual",
    toneDirect: "Direct",
    toneEmpathetic: "Empathetic",
    toneFormal: "Formal",
    toneFriendly: "Friendly",
    toneHumorous: "Humorous",
    tonePassive: "Passive",
    tonePersuasive: "Persuasive",
    tonePolite: "Polite",
    toneProfessional: "Professional",
    // Language names
    langEnglish: "English",
    langSpanish: "Spanish",
    langHindi: "Hindi",
    langKorean: "Korean",
    langJapanese: "Japanese",
    langGerman: "German",
    langEnNorthAmerica: "North American English",
    langEnAfrican: "African English",
    langEnEuropean: "European English",
    langEnAsian: "Asian English",
    langEnAustralian: "Australian English",
    langEsNorthAmerica: "North American Spanish",
    langEsSouthAmerica: "South American Spanish",
    langEsEuropean: "European Spanish",
    langEsCaribbean: "African or Caribbean Spanish",
    langEsAsian: "Asian Spanish",
    // Blog
    blog: "Blog",
    blogSubtitle: "Articles and tips for better communication",
    writeAPost: "Manage Posts",
    noBlogPosts: "No blog posts yet. Check back soon!",
    all: "All",
    readMore: "Read More",
    publishedOn: "Published on",
    backToBlog: "Back to Blog",
    relatedArticles: "Related Articles",
    tableOfContents: "In This Article",
    views: "views"
  },
  es: {
    literalMeaning: "Significado Literal",
    actualMeaning: "Significado Real",
    examplesInContext: "Ejemplos en Contexto",
    helpfulTips: "Consejos Útiles",
    phraseThesaurus: "Tesauro de Frases",
    similarWords: "Palabras similares y sinónimos para esta frase",
    relatedPhrases: "Frases Relacionadas",
    clickToExplore: "Haz clic para explorar frases relacionadas en nuestra colección",
    searchPlaceholder: "Ingresa una frase que te gustaría entender",
    searchResults: "Resultados de Búsqueda",
    found: "Se encontraron",
    phrase: "frase",
    phrases: "frases",
    for: "para",
    clearSearch: "Limpiar Búsqueda",
    search: "Buscar",
    noPhrasesFound: "No se encontraron frases",
    tryDifferent: "Intenta con otras palabras o explora las categorías",
    requestPhrase: "Solicitar esta frase",
    browseByCategory: "Explorar por Categoría",
    selectCategory: "Selecciona una categoría para explorar frases",
    selectSubcategory: "Selecciona una subcategoría para ver frases",
    backToCategories: "Volver a Categorías",
    backTo: "Volver a",
    inThisCategory: "en esta categoría",
    noPhrases: "No hay frases todavía",
    comingSoon: "¡Esta categoría viene pronto! Ayúdanos sugiriendo frases.",
    suggestPhrase: "Sugerir una frase",
    definitionsLanguage: "Definiciones en Español",
    toggleLanguageLabel: "Cambiar idioma de definiciones",
    translating: "Traduciendo...",
    backToAllPhrases: "Volver a todas las frases",
    back: "Atrás",
    // Hero section translations
    heroTagline: "Entiende la comunicación implícita con claridad",
    heroTitle1: "Descifra los Significados Ocultos en las",
    heroTitle2: "Conversaciones Cotidianas",
    heroDescription: "Una herramienta de apoyo con definiciones reales para significados implícitos, modismos, sarcasmo, y frases generacionales, regionales y sociales que las personas neurotípicas suelen usar. Esta herramienta fue creada por una madre soltera neurodivergente de 4 hijos neurodivergentes, ¡pero esta aplicación es para cualquiera que la encuentre útil!",
    heroSearchPrompt: "Busca una frase o desplázate hacia abajo para ver frases por categoría",
    // Navigation
    previous: "Anterior",
    next: "Siguiente",
    // Header
    decodeCommunication: "Descifra la Comunicación",
    menu: "Menú",
    home: "Inicio",
    about: "Acerca de",
    feedbackRequest: "Comentarios / Solicitar Frase",
    dinoLlamaTimer: "Temporizador Dino Llama",
    todoList: "Lista de Tareas",
    subscription: "Suscripción",
    manageSubscription: "Gestionar Suscripción",
    signIn: "Iniciar Sesión",
    signOut: "Cerrar Sesión",
    accountSettings: "Configuración de Cuenta",
    leaderboard: "Clasificación",
    trial: "Prueba",
    premium: "VIP Access",
    // Multi-category
    foundInMultipleCategories: "Encontrado en múltiples categorías:",
    meaningsByCategory: "Significados por Categoría",
    meaning: "Significado",
    context: "Contexto",
    examples: "Ejemplos",
    tips: "Consejos",
    // Feedback Page
    feedbackTitle: "Comparte tus Comentarios",
    feedbackDescription: "Ayúdanos a mejorar o solicita nuevas frases",
    feedbackTab: "Comentarios",
    requestPhraseTab: "Solicitar Frase",
    yourName: "Tu Nombre",
    yourEmail: "Tu Correo Electrónico",
    yourFeedback: "Tus Comentarios",
    shareThoughts: "Comparte tus pensamientos, sugerencias o reporta problemas...",
    submitFeedback: "Enviar Comentarios",
    phraseYouHeard: "Frase que Escuchaste",
    enterPhrase: "Ingresa la frase que te gustaría que agreguemos...",
    whatYouThink: "¿Qué crees que significa literalmente?",
    whatItActuallyMeans: "¿Qué crees que realmente significa?",
    additionalContext: "Contexto Adicional",
    whereYouHeard: "¿Dónde la escuchaste? Cualquier otra información útil...",
    submitRequest: "Enviar Solicitud",
    // About Page
    aboutTitle: "Acerca de ClearCommunicationApp",
    aboutAppDescription: "Entendiendo la comunicación implícita para todos",
    // Timer Page
    timerTitle: "Temporizador Dino Llama",
    timerDescription: "Un temporizador de productividad divertido",
    setTimer: "Establecer Temporizador",
    minutes: "minutos",
    startTimer: "Iniciar Temporizador",
    pauseTimer: "Pausar Temporizador",
    resetTimer: "Reiniciar Temporizador",
    timerFinished: "¡Temporizador terminado!",
    // Todo Page
    todoTitle: "Lista de Tareas",
    todoDescription: "Organiza tus tareas",
    addTask: "Agregar Tarea",
    newTask: "Nueva tarea...",
    noTasks: "No hay tareas todavía. ¡Agrega una arriba!",
    clearCompleted: "Limpiar Completadas",
    // Subscription & VIP Access
    vipAccess: "Acceso VIP",
    definitionPreviewsAvailable: "Las vistas previas de definiciones están disponibles con suscripción",
    subscribeToUnlock: "Suscríbete para desbloquear",
    startFreeTrial: "Comenzar Prueba Gratis",
    freeTrialDays: "7 días de prueba gratis",
    thenPerMonth: "luego $1.99/mes",
    unlimitedAccess: "Acceso ilimitado a todas las frases",
    cancelAnytime: "Cancela en cualquier momento",
    freeViewsRemaining: "vistas gratuitas restantes",
    upgradeForUnlimited: "Actualiza para acceso ilimitado",
    becomeVipToSee: "Para ver vista previa de definiciones hazte VIP (clic para ver definición completa)",
    // Free phrase limit
    freeLimitReached: "Límite Gratuito Alcanzado",
    freePhrasesExhausted: "Frases Gratuitas Agotadas",
    youveViewedFree: "Has visto",
    freePhrases: "frases gratis",
    upgradeToUnlock: "Actualiza para desbloquear todas las",
    plusPhrases: "+ frases!",
    subscribeToUnlockAll: "Suscríbete para desbloquear acceso ilimitado a todas las",
    phraseDefinitions: "definiciones de frases",
    startSevenDayTrial: "Comenzar Prueba Gratis de 7 Días",
    starting: "Iniciando...",
    subscribePerMonth: "Suscríbete por $1.99/mes",
    signInToContinue: "para continuar con tu prueba gratis",
    maybeLater: "Quizás Después",
    sevenDayTrialIncluded: "Prueba gratis de 7 días incluida • Cancela cuando quieras",
    freePhrasesRemaining: "frases gratis restantes",
    freePhraseRemaining: "frase gratis restante",
    // Daily limit
    dailyLimitReached: "Límite Diario Alcanzado",
    dailyPhrasesExhausted: "Frases Diarias Agotadas",
    freePhrasesToday: "frases gratis hoy",
    dailyLimitMessage: "Disfruta sin anuncios con VIP",
    comeBackTomorrow: "¡Disfruta de una experiencia limpia sin anuncios con VIP!",
    goVipUnlimited: "¡Hazte VIP para frases ilimitadas y recursos diarios!",
    dailyViewsReset: "Las vistas diarias se reinician a medianoche",
    // FAQs
    faqTitle: "Preguntas Frecuentes",
    faqHowTrialWorks: "¿Cómo funciona la prueba gratuita?",
    faqTrialAnswer: "Cuando inicias tu suscripción VIP, obtienes 7 días completamente gratis. Tu tarjeta no será cargada hasta el día 8. Puedes cancelar en cualquier momento durante la prueba.",
    faqWhatIncluded: "¿Qué está incluido?",
    faqIncludedAnswer: "Con Acceso VIP disfrutas de una experiencia sin anuncios, más acceso anticipado a nuevas funciones y soporte premium.",
    // App Store
    comingSoonAppStore: "¡Próximamente en una tienda de aplicaciones cerca de ti!",
    // Feedback form fields
    enterName: "Ingresa tu nombre",
    emailAddress: "Correo Electrónico",
    emailPlaceholder: "tu@email.com",
    feedbackPlaceholder: "Comparte tus pensamientos, sugerencias o reporta problemas...",
    sending: "Enviando...",
    phrasePlaceholder: "ej., 'Deberíamos hacer esto de nuevo algún día'",
    literalMeaningPlaceholder: "Lo que las palabras literalmente dicen",
    impliedMeaningPlaceholder: "Lo que el hablante realmente quiere decir",
    additionalNotes: "Notas Adicionales",
    notesPlaceholder: "Contexto, ejemplos, o dónde escuchaste esta frase...",
    submitPhraseRequest: "Enviar Solicitud de Frase",
    emailDirectly: "También puedes enviarnos un email a",
    optional: "(Opcional)",
    // FAQs Page
    faqsPage: "Preguntas Frecuentes",
    faqSectionBreakdown: "¿Para qué es cada una de las secciones en la biblioteca de frases?",
    faqSectionBreakdownAnswer: "Cada entrada de frase incluye varias secciones para ayudarte a entender completamente la frase:\n\n• **Significado Literal** — Lo que las palabras técnicamente dicen (la definición del diccionario)\n\n• **Significado Real** — Lo que el hablante realmente quiere decir (el significado implícito o de jerga)\n\n• **Contexto** — Cuándo y cómo se usa típicamente la frase\n\n• **Ejemplos** — Escenarios de uso en el mundo real mostrando la frase en acción\n\n• **Consejos** — Orientación útil para entender y responder apropiadamente",
    faqWhyBuilt: "¿Por qué se creó esta aplicación?",
    faqWhyBuiltAnswer: "Esta aplicación fue creada por una mamá neurodivergente que entiende los desafíos de descifrar la comunicación implícita. Está diseñada para ayudar a cualquiera que quiera entender mejor los significados ocultos en las conversaciones cotidianas.",
    // Thought Interpreter
    thoughtInterpreter: "Intérprete de Pensamientos",
    interpreterSubtitle: "Escribe lo que intentas transmitir y el Detective Dino te ayudará a reformularlo con claridad",
    remainingToday: "restantes hoy",
    inputLanguage: "Idioma de entrada",
    outputLanguage: "Idioma de salida",
    whatDoYouWantToSay: "¿Qué quieres decir?",
    interpreterPlaceholder: "No puedo trabajar este fin de semana porque tengo un evento familiar",
    tone: "Tono",
    interpret: "Interpretar",
    interpreting: "Interpretando...",
    interpretedVersion: "Versión Interpretada",
    // Decodificador Neurotípico (mayo 2026)
    modeRephrase: "Replantear mi pensamiento",
    modeDecode: "Decodificar sus palabras",
    decoderTitle: "Decodificador Neurotípico",
    decoderSubtitle: "Pega lo que dijeron. Obtén lo que realmente quisieron decir + una respuesta que puedes usar.",
    decoderInputLabel: "Pega su mensaje",
    decoderPlaceholder: "Sería genial si pudieras revisar el entregable cuando tengas tiempo.",
    decode: "Decodificar",
    decoding: "Decodificando…",
    decoderHeading: "Lo que realmente quieren decir",
    decoderLiteralLabel: "Significado literal",
    decoderIntentLabel: "Por qué lo dijeron así",
    decoderResponseLabel: "Respuesta que puedes usar",
    decoderDisclaimer: "Lectura asistida por IA — el contexto importa. Úsalo como punto de partida, no como veredicto.",
    interpretedVersion: "Versión interpretada",
    copy: "Copiar",
    copied: "Copiado",
    tryAgain: "Otra versión",
    recentHistory: "Historial reciente",
    dailyLimitVip: "Límite diario alcanzado. ¡Mejora a VIP para acceso ilimitado!",
    pleaseTypeSomething: "Por favor escribe algo primero",
    failedToInterpret: "Error al interpretar. Por favor intenta de nuevo.",
    save: "Guardar",
    saved: "Guardado",
    savedToFavorites: "Guardado en tus reformulaciones",
    alreadySaved: "Ya está guardado",
    signInToSave: "Inicia sesión para guardar reformulaciones",
    failedToSave: "No se pudo guardar. Intenta de nuevo.",
    myRephrases: "Pensamientos guardados que fueron interpretados",
    noSavedRephrases: "Aún no has guardado reformulaciones. Usa el Intérprete y toca Guardar para conservar frases que te gusten.",
    deleteRephrase: "Eliminar reformulación",
    confirmDeleteRephrase: "¿Eliminar esta reformulación guardada?",
    rephraseDeleted: "Reformulación eliminada",
    // Tones
    toneCasual: "Casual",
    toneDirect: "Directo",
    toneEmpathetic: "Empático",
    toneFormal: "Formal",
    toneFriendly: "Amigable",
    toneHumorous: "Humorístico",
    tonePassive: "Pasivo",
    tonePersuasive: "Persuasivo",
    tonePolite: "Cortés",
    toneProfessional: "Profesional",
    // Language names
    langEnglish: "Inglés",
    langSpanish: "Español",
    langHindi: "Hindi",
    langKorean: "Coreano",
    langJapanese: "Japonés",
    langGerman: "Alemán",
    langEnNorthAmerica: "Inglés Norteamericano",
    langEnAfrican: "Inglés Africano",
    langEnEuropean: "Inglés Europeo",
    langEnAsian: "Inglés Asiático",
    langEnAustralian: "Inglés Australiano",
    langEsNorthAmerica: "Español Norteamericano",
    langEsSouthAmerica: "Español Sudamericano",
    langEsEuropean: "Español Europeo",
    langEsCaribbean: "Español Africano o Caribeño",
    langEsAsian: "Español Asiático",
    // Blog
    blog: "Blog",
    blogSubtitle: "Artículos y consejos para comunicarse mejor",
    writeAPost: "Administrar artículos",
    noBlogPosts: "No hay artículos todavía. ¡Vuelve pronto!",
    all: "Todos",
    readMore: "Leer más",
    publishedOn: "Publicado el",
    backToBlog: "Volver al Blog",
    relatedArticles: "Artículos relacionados",
    tableOfContents: "En este artículo",
    views: "vistas"
  }
};

// Cache for translated content - keyed by category ID and language
const categoryTranslationCache = new Map();
const phraseTranslationCache = new Map();

export const LanguageProvider = ({ children }) => {
  const [definitionLanguage, setDefinitionLanguage] = useState(() => {
    if (typeof window === 'undefined') return 'en';
    // URL ?lang=es|en takes precedence over localStorage so
    // - Googlebot can index distinct language versions of every page
    // - users can deep-link to a specific language without flipping their preference
    try {
      const urlLang = new URLSearchParams(window.location.search).get('lang');
      if (urlLang === 'es' || urlLang === 'en') return urlLang;
    } catch { /* ignore malformed URLs */ }
    return localStorage.getItem('definitionLanguage') || 'en';
  });
  
  const [isTranslating, setIsTranslating] = useState(false);
  const [translatedCategories, setTranslatedCategories] = useState({});

  useEffect(() => {
    localStorage.setItem('definitionLanguage', definitionLanguage);
    // Mark <html lang="..."> so the CSS `:lang(es)` selectors that wrap long
    // Spanish words inside cards/buttons actually match. Without this Spanish
    // copy can blow past container widths and cause horizontal scroll.
    if (typeof document !== 'undefined') {
      document.documentElement.lang = definitionLanguage === 'es' ? 'es' : 'en';
    }
  }, [definitionLanguage]);

  const toggleLanguage = () => {
    // Clear ALL translation caches when toggling to prevent stale data
    phraseTranslationCache.clear();
    categoryTranslationCache.clear();
    setTranslatedCategories({});
    
    // Clear any sessionStorage caches as well
    if (typeof window !== 'undefined') {
      sessionStorage.removeItem('phraseTranslationCache');
      sessionStorage.removeItem('categoryTranslationCache');
      sessionStorage.removeItem('translatedPhrases');
      sessionStorage.removeItem('translatedCategories');
    }
    
    // Show the translation loader animation (small inline dino/llama running)
    setIsTranslating(true);
    
    // Change language after a brief delay so users see the cute animation
    const newLang = definitionLanguage === 'en' ? 'es' : 'en';
    
    // Show animation for 1.5 seconds, then switch language (NO page reload)
    setTimeout(() => {
      setDefinitionLanguage(newLang);
      setIsTranslating(false);
    }, 1500);
  };

  // Get UI label translation
  const t = (key) => {
    return uiTranslations[definitionLanguage]?.[key] || uiTranslations.en[key] || key;
  };

  const isSpanish = definitionLanguage === 'es';

  // Translate category names - uses stored translations first, then API
  const translateCategories = useCallback(async (categories) => {
    if (definitionLanguage === 'en' || !categories || categories.length === 0) {
      return categories;
    }
    
    // For Spanish, use stored nameEs/descriptionEs fields if available
    const translatedCategories = categories.map(cat => {
      // Use stored Spanish translations if available
      if (cat.nameEs || cat.descriptionEs) {
        return {
          ...cat,
          name: cat.nameEs || cat.name,
          description: cat.descriptionEs || cat.description,
          originalName: cat.name,
          originalDescription: cat.description,
          hasStoredTranslation: true,  // Mark as having stored translation
        };
      }
      
      // Check cache for API translations
      const cacheKey = `cat:${cat.id}:${definitionLanguage}`;
      if (categoryTranslationCache.has(cacheKey)) {
        return categoryTranslationCache.get(cacheKey);
      }
      
      return { ...cat, hasStoredTranslation: false };
    });
    
    // Find categories that need API translation (no stored Spanish and not cached)
    const uncachedCategories = translatedCategories.filter(cat => {
      const cacheKey = `cat:${cat.id}:${definitionLanguage}`;
      return !cat.hasStoredTranslation && !categoryTranslationCache.has(cacheKey);
    });
    
    // If all have stored translations or are cached, return
    if (uncachedCategories.length === 0) {
      return translatedCategories;
    }
    
    setIsTranslating(true);
    try {
      // Get all category names and descriptions that need translation
      const textsToTranslate = [];
      uncachedCategories.forEach(cat => {
        textsToTranslate.push(cat.name);
        if (cat.description) textsToTranslate.push(cat.description);
      });
      
      const translations = await translateBatch(textsToTranslate, definitionLanguage);
      
      // Apply translations and cache
      uncachedCategories.forEach(cat => {
        const translatedCat = {
          ...cat,
          name: translations[cat.name] || cat.name,
          description: translations[cat.description] || cat.description,
          originalName: cat.name,
          originalDescription: cat.description,
        };
        const cacheKey = `cat:${cat.id}:${definitionLanguage}`;
        categoryTranslationCache.set(cacheKey, translatedCat);
      });
      
      // Return final translated list
      return categories.map(cat => {
        // Use stored Spanish translations first
        if (cat.nameEs || cat.descriptionEs) {
          return {
            ...cat,
            name: cat.nameEs || cat.name,
            description: cat.descriptionEs || cat.description,
            originalName: cat.name,
            originalDescription: cat.description,
          };
        }
        // Then check cache
        const cacheKey = `cat:${cat.id}:${definitionLanguage}`;
        return categoryTranslationCache.get(cacheKey) || cat;
      });
    } catch (error) {
      console.error('Failed to translate categories:', error);
      return translatedCategories;
    } finally {
      setIsTranslating(false);
    }
  }, [definitionLanguage]);

  // Translate a single category by ID
  const translateSingleCategory = useCallback(async (category) => {
    if (definitionLanguage === 'en' || !category) {
      return category;
    }
    
    const cacheKey = `cat:${category.id}:${definitionLanguage}`;
    if (categoryTranslationCache.has(cacheKey)) {
      return categoryTranslationCache.get(cacheKey);
    }
    
    try {
      const textsToTranslate = [category.name];
      if (category.description) textsToTranslate.push(category.description);
      
      const translations = await translateBatch(textsToTranslate, definitionLanguage);
      
      const translatedCat = {
        ...category,
        name: translations[category.name] || category.name,
        description: translations[category.description] || category.description,
        originalName: category.name,
        originalDescription: category.description,
      };
      
      categoryTranslationCache.set(cacheKey, translatedCat);
      return translatedCat;
    } catch (error) {
      console.error('Failed to translate category:', error);
      return category;
    }
  }, [definitionLanguage]);

  // Translate a phrase's definition fields
  // Supports bidirectional translation:
  // - When in Spanish mode (isSpanish=true): translate EN->ES
  // - When in English mode viewing Spanish category phrases: NO translation needed (content is already in English)
  const translatePhraseContent = useCallback(async (phrase, forceTranslate = false) => {
    if (!phrase) {
      return phrase;
    }
    
    // Check if this is a Spanish category phrase (es-* categories)
    const isSpanishCategoryPhrase = phrase.category?.startsWith('es-') || 
      phrase.category?.startsWith('spanish-');
    
    // IMPORTANT: Spanish category phrases have their definitions stored in ENGLISH in the database.
    // Only translate when user switches to Spanish mode.
    // In English mode, return the phrase as-is (it's already in English).
    
    // Skip translation if in English mode (content is already in English)
    if (definitionLanguage === 'en' && !forceTranslate) {
      return phrase;
    }
    
    // Only translate when in Spanish mode
    const targetLang = 'es';
    
    const cacheKey = `phrase:${phrase.id}:${targetLang}`;
    if (phraseTranslationCache.has(cacheKey)) {
      return phraseTranslationCache.get(cacheKey);
    }
    
    try {
      const translated = await translatePhrase(phrase, targetLang);
      
      // Map translated fields
      // IMPORTANT: 
      // - Examples should NEVER be translated (stay in original language for authenticity)
      // - Tips are in English, translate them to Spanish
      
      const result = {
        ...phrase,
        literalMeaning: translated.literalMeaning_translated || phrase.literalMeaning,
        actualMeaning: translated.actualMeaning_translated || phrase.actualMeaning,
        context: translated.context_translated || phrase.context,
        // Keep examples in original language - NEVER translate
        examples: phrase.examples,
        // Translate tips to Spanish
        tips: translated.tips_translated || phrase.tips,
        // Keep originals for reference
        originalLiteralMeaning: phrase.literalMeaning,
        originalActualMeaning: phrase.actualMeaning,
        originalContext: phrase.context,
        originalExamples: phrase.examples,
        originalTips: phrase.tips,
      };
      
      phraseTranslationCache.set(cacheKey, result);
      return result;
    } catch (error) {
      console.error('Failed to translate phrase:', error);
      return phrase;
    }
  }, [definitionLanguage, isSpanish]);

  // Get translated text (for category names on cards, etc.)
  const getTranslatedText = useCallback((text, translationMap) => {
    if (definitionLanguage === 'en' || !translationMap) {
      return text;
    }
    return translationMap[text] || text;
  }, [definitionLanguage]);

  return (
    <LanguageContext.Provider value={{ 
      definitionLanguage, 
      setDefinitionLanguage, 
      toggleLanguage,
      t,
      isSpanish,
      isTranslating,
      translateCategories,
      translateSingleCategory,
      translatePhraseContent,
      getTranslatedText,
      translatedCategories,
    }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
